
import React from "react";

import { useNavigate } from "react-router-dom";

import { useGetOutstandingInvoicesQuery } from "../../slices/billingDashboardApiSlice";

const OutstandingInvoicesScreen = () => {
  const navigate = useNavigate();

  const { data, isLoading, error } = useGetOutstandingInvoicesQuery();

  const billing = data?.data;

  const invoices = billing?.invoices || [];

  const summary = billing?.summary || {
    amount: 0,
    taxableAmount: 0,
    taxAmount: 0,
    paidAmount: 0,
    count: 0,
  };

  // =====================================================
  // Helpers
  // =====================================================

  const formatCurrency = (amount) => {
    return `₹${Number(amount || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const formatDate = (date) => {
    if (!date) return "-";

    return new Date(date).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const getBalance = (invoice) => {
    return Math.max(
      Number(invoice.total || 0) - Number(invoice.paidAmount || 0),
      0,
    );
  };

  const isOverdue = (invoice) => {
    if (!invoice.dueDate) return false;

    return (
      invoice.status === "pending" && new Date(invoice.dueDate) < new Date()
    );
  };

  const getStatusBadge = (status, overdue = false) => {
    if (overdue) {
      return "bg-red-100 text-red-700";
    }

    const styles = {
      pending: "bg-yellow-100 text-yellow-700",
      paid: "bg-green-100 text-green-700",
      cancelled: "bg-red-100 text-red-700",
      overdue: "bg-red-100 text-red-700",
    };

    return styles[status] || "bg-gray-100 text-gray-700";
  };

  // =====================================================
  // Loading
  // =====================================================

  if (isLoading) {
    return (
      <div className="flex min-h-[300px] items-center justify-center">
        <p className="text-gray-500">Loading outstanding invoices...</p>
      </div>
    );
  }

  // =====================================================
  // Error
  // =====================================================

  if (error) {
    return (
      <div className="rounded-lg bg-red-50 p-5 text-red-700">
        {error?.data?.message ||
          error?.error ||
          "Unable to load outstanding invoices"}
      </div>
    );
  }

  // =====================================================
  // Render
  // =====================================================

  return (
    <div>
      {/* =====================================================
          Header
      ===================================================== */}

      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">
          Outstanding Invoices
        </h1>

        <p className="mt-1 text-gray-500">Pending invoices awaiting payment</p>
      </div>

      {/* =====================================================
          Summary
      ===================================================== */}

      <div className="mb-6 grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-4">
        <SummaryCard
          title="Outstanding Invoices"
          value={summary.count}
          color="bg-blue-600"
        />

        <SummaryCard
          title="Outstanding Amount"
          value={formatCurrency(summary.amount)}
          color="bg-red-600"
        />

        <SummaryCard
          title="Taxable Amount"
          value={formatCurrency(summary.taxableAmount)}
          color="bg-indigo-600"
        />

        <SummaryCard
          title="GST Amount"
          value={formatCurrency(summary.taxAmount)}
          color="bg-purple-600"
        />
      </div>

      {/* =====================================================
          Payment Summary
      ===================================================== */}

      <div className="mb-6 rounded-lg bg-white shadow">
        <div className="border-b px-6 py-4">
          <h3 className="font-semibold text-lg text-gray-800">
            Outstanding Billing Summary
          </h3>

          <p className="mt-1 text-sm text-gray-500">
            Summary of all pending invoices
          </p>
        </div>

        <div className="grid grid-cols-1 gap-6 p-6 md:grid-cols-3">
          <SummaryDetail
            label="Total Invoice Amount"
            value={formatCurrency(summary.amount)}
          />

          <SummaryDetail
            label="Already Paid"
            value={formatCurrency(summary.paidAmount)}
            valueClass="text-green-600"
          />

          <SummaryDetail
            label="Balance Outstanding"
            value={formatCurrency(
              Math.max(
                Number(summary.amount || 0) - Number(summary.paidAmount || 0),
                0,
              ),
            )}
            valueClass="text-red-600"
          />
        </div>
      </div>

      {/* =====================================================
          Invoice Table
      ===================================================== */}

      <div className="overflow-hidden rounded-lg bg-white shadow">
        <div className="border-b px-6 py-4">
          <h3 className="font-semibold text-lg text-gray-800">
            Outstanding Invoice List
          </h3>

          <p className="mt-1 text-sm text-gray-500">
            {invoices.length} pending invoice
            {invoices.length === 1 ? "" : "s"}
          </p>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead className="bg-gray-100 text-gray-700">
              <tr>
                <th className="px-4 py-3">Invoice</th>

                <th className="px-4 py-3">Coaching Center</th>

                <th className="px-4 py-3">Invoice Date</th>

                <th className="px-4 py-3">Due Date</th>

                <th className="px-4 py-3 text-right">Taxable</th>

                <th className="px-4 py-3 text-right">GST</th>

                <th className="px-4 py-3 text-right">Total</th>

                <th className="px-4 py-3 text-right">Paid</th>

                <th className="px-4 py-3 text-right">Balance</th>

                <th className="px-4 py-3">Status</th>

                <th className="px-4 py-3">Action</th>
              </tr>
            </thead>

            <tbody>
              {invoices.length === 0 ? (
                <tr>
                  <td
                    colSpan="11"
                    className="px-4 py-10 text-center text-gray-500"
                  >
                    No outstanding invoices found
                  </td>
                </tr>
              ) : (
                invoices.map((invoice) => {
                  const balance = getBalance(invoice);
                  const overdue = isOverdue(invoice);

                  return (
                    <tr key={invoice._id} className="border-b hover:bg-gray-50">
                      {/* Invoice */}

                      <td className="px-4 py-3">
                        <p className="font-semibold text-gray-800">
                          {invoice.invoiceNumber || "-"}
                        </p>

                        {invoice.gstType && (
                          <p className="text-xs text-gray-500">
                            {invoice.gstType}
                          </p>
                        )}
                      </td>

                      {/* Coaching Center */}

                      <td className="px-4 py-3">
                        <div className="font-medium">
                          {invoice.coachingCenter?.name ||
                            invoice.coachingCenterName ||
                            "-"}
                        </div>

                        {invoice.coachingCenter?.email && (
                          <div className="text-xs text-gray-500">
                            {invoice.coachingCenter.email}
                          </div>
                        )}

                        {invoice.coachingCenter?.phone && (
                          <div className="text-xs text-gray-500">
                            {invoice.coachingCenter.phone}
                          </div>
                        )}
                      </td>

                      {/* Invoice Date */}

                      <td className="px-4 py-3">
                        {formatDate(invoice.invoiceDate)}
                      </td>

                      {/* Due Date */}

                      <td className="px-4 py-3">
                        {formatDate(invoice.dueDate)}

                        {overdue && (
                          <div className="mt-1">
                            <span className="inline-block rounded-full bg-red-100 px-2 py-1 text-xs font-medium text-red-700">
                              Overdue
                            </span>
                          </div>
                        )}
                      </td>

                      {/* Taxable Amount */}

                      <td className="px-4 py-3 text-right">
                        {formatCurrency(invoice.taxableAmount)}
                      </td>

                      {/* GST */}

                      <td className="px-4 py-3 text-right">
                        <div>{formatCurrency(invoice.totalTaxAmount)}</div>

                        {invoice.gstRate !== undefined && (
                          <div className="text-xs text-gray-500">
                            {invoice.gstRate || 0}%
                          </div>
                        )}
                      </td>

                      {/* Total */}

                      <td className="px-4 py-3 text-right font-semibold">
                        {formatCurrency(invoice.total)}
                      </td>

                      {/* Paid */}

                      <td className="px-4 py-3 text-right">
                        {formatCurrency(invoice.paidAmount)}
                      </td>

                      {/* Balance */}

                      <td className="px-4 py-3 text-right font-semibold text-red-600">
                        {formatCurrency(balance)}
                      </td>

                      {/* Status */}

                      <td className="px-4 py-3">
                        <span
                          className={`rounded-full px-3 py-1 text-xs font-medium ${getStatusBadge(
                            invoice.status,
                            overdue,
                          )}`}
                        >
                          {overdue ? "overdue" : invoice.status || "unknown"}
                        </span>
                      </td>

                      {/* Action */}

                      <td className="px-4 py-3">
                        <button
                          type="button"
                          className="
                            rounded
                            border
                            border-blue-600
                            px-3
                            py-1
                            text-sm
                            text-blue-600
                            hover:bg-blue-600
                            hover:text-white
                          "
                          onClick={() =>
                            navigate(`/billing/invoices/${invoice._id}`)
                          }
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>

            {invoices.length > 0 && (
              <tfoot>
                <tr className="bg-gray-50 font-semibold">
                  <td colSpan="4" className="px-4 py-4 text-right">
                    Total
                  </td>

                  <td className="px-4 py-4 text-right">
                    {formatCurrency(summary.taxableAmount)}
                  </td>

                  <td className="px-4 py-4 text-right">
                    {formatCurrency(summary.taxAmount)}
                  </td>

                  <td className="px-4 py-4 text-right">
                    {formatCurrency(summary.amount)}
                  </td>

                  <td className="px-4 py-4 text-right text-green-600">
                    {formatCurrency(summary.paidAmount)}
                  </td>

                  <td className="px-4 py-4 text-right text-red-600">
                    {formatCurrency(
                      Math.max(
                        Number(summary.amount || 0) -
                          Number(summary.paidAmount || 0),
                        0,
                      ),
                    )}
                  </td>

                  <td colSpan="2" />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
};

// =====================================================
// Summary Card
// =====================================================

const SummaryCard = ({ title, value, color }) => {
  return (
    <div
      className={`
        ${color}
        rounded-lg
        p-5
        text-white
        shadow
      `}
    >
      <p className="text-sm opacity-90">{title}</p>

      <h2 className="mt-2 text-2xl font-bold">{value}</h2>
    </div>
  );
};

// =====================================================
// Summary Detail
// =====================================================

const SummaryDetail = ({ label, value, valueClass = "text-gray-800" }) => {
  return (
    <div>
      <p className="text-sm text-gray-500">{label}</p>

      <p
        className={`
          mt-1
          text-xl
          font-bold
          ${valueClass}
        `}
      >
        {value}
      </p>
    </div>
  );
};

export default OutstandingInvoicesScreen;
